// src/lib/components/molecules/AtlasTreeMobile/AtlasTreeMobile.tsx

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';

import NodeLabel from '../NodeLabel/NodeLabel';
import './AtlasTreeMobile.scss';
import { Toast } from 'primereact/toast';
import { Skeleton } from 'primereact/skeleton';
import { PermissionList } from '../../../../../types/sharedTypes';
import AddSphere from '../../AddSphere/AddSphere';
import AddTemplate from '../../AddTemplate/AddTemplate';
import DialogWrapper from '../../DialogWrapper/DialogWrapper';
import { Sphere } from 'types/global';
import { SphereNode } from 'types/context';
import { fetchPermissionList } from '@center-platform/spheres/sphere';
import { useContextTree } from '../../../../hooks/useContextTree';
import { useSchemaType } from '../../../../hooks/useSchemaType';
import { useAvailableTypes } from '../../../../hooks/useAvailableTypes';
import AtlasEmptyStateMolecule from '../AtlasEmptyState/AtlasEmptyStateMolecule';
import { getRecentTypes } from '../AtlasEmptyState/atlasRecentTypes';
import { sphereNodeToSphere } from '../../../../adapters/sphereNodeToSphere';
import { AtlasViewContext } from '../../../organisms/AtlasComponent/AtlasViewContext';
import { deriveEnrichmentFromMolecules, deriveColumnPropertiesFromMolecules } from '../AtlasTree/enrichmentUtils';
import EditSphere from '../EditSphere/EditSphere';
import { useApiClientsOrNull } from '../../../../context/ApiClientsContext';
import { useTranslation } from 'localization';
import MobileAtlasHeader from '../MobileAtlasHeader/MobileAtlasHeaderMolecule';
import debounce from 'lodash.debounce';

interface AtlasTreeMobileProps {
  sphereId: string;
  pageLimit: number | null;
  onChatBubbleClick?: (sphereId: string) => void;
  showToolbar?: boolean; // Whether to show toolbar/chat based on showMetasphereToolbar setting
}

const AtlasTreeMobile: React.FC<AtlasTreeMobileProps> = React.memo(({ sphereId, pageLimit = 10, onChatBubbleClick, showToolbar = true }) => {
  const apiClients = useApiClientsOrNull();
  const { t } = useTranslation();
  const [expandedKeys, setExpandedKeys] = useState<{ [key: string]: boolean }>({});
  const [breadcrumbPath, setBreadcrumbPath] = useState<string>('');
  const [nodeSphere, setNodeSphere] = useState<Sphere | null>(null);
  const [nodeParentSphere, setNodeParentSphere] = useState<Sphere | null>(null);
  const [nodeGrandparentSphere, setNodeGrandparentSphere] = useState<Sphere | null>(null);
  const treeRef = useRef<HTMLDivElement>(null);
  const observersRef = useRef<{ [key: string]: IntersectionObserver }>({});
  const [editViewMode, setEditViewMode] = useState<boolean>(false);
  const [featuredSphereId, setFeaturedSphereId] = useState(sphereId);
  const toast = useRef<Toast>(null);
  const fetchingRef = useRef<{ [key: string]: boolean }>({});
  const context = React.useContext(AtlasViewContext);
  const { contextSystemName: firstContext, viewConfig } = context ?? { contextSystemName: '', viewConfig: { type: 'atlas' as const, nodeMolecules: [] } };
  const baseSchemaType = undefined;
  const { schema: baseSchema } = useSchemaType(baseSchemaType);

  const [addSphereDialogVisible, setAddSphereDialogVisible] = useState(false);
  const [editSphereDialogVisible, setEditSphereDialogVisible] = useState(false);
  const [templateDialogVisible, setTemplateDialogVisible] = useState(false);
  const [selectedTemplateSchema, setSelectedTemplateSchema] = useState<any>(null);
  const [selectedSphereType, setSelectedSphereType] = useState<any>(null);
  const [nodeNotifications, setNodeNotifications] = useState<{
    [nodeId: string]: {
      type: 'success' | 'error';
      message: string;
      timer?: number;
    };
  }>({});

  // Use context tree hook for data fetching
  const {
    roots: contextRoots,
    loading: _contextLoading,
    error: _contextError,
    refresh: contextRefresh,
    hasMore: contextHasMore,
    loadMore: contextLoadMore,
  } = useContextTree(apiClients, {
    systemName: firstContext ?? '',
    rootSphereId: featuredSphereId,
    limit: pageLimit ?? 50,
    enrichmentOverrides: deriveEnrichmentFromMolecules(viewConfig.nodeMolecules ?? []),
    columnProperties: deriveColumnPropertiesFromMolecules(viewConfig.nodeMolecules ?? []),
  });

  // Convert SphereNode tree to legacy Sphere tree for downstream components
  const nodesData: Sphere[] = useMemo(
    () => (contextRoots ?? []).map(sphereNodeToSphere),
    [contextRoots]
  );

  const isChildless = useMemo(
    () =>
      !_contextLoading &&
      !_contextError &&
      nodesData.length > 0 &&
      contextRoots != null &&
      contextRoots.length > 0 &&
      contextRoots[0].children.length === 0 &&
      !contextHasMore(nodesData[0].id),
    [_contextLoading, _contextError, nodesData, contextRoots, contextHasMore],
  );

  const emptyStateOrgId =
    (nodesData[0]?.properties?.organization_id as string | undefined) ??
    (nodesData[0]?.properties?.sphereId as string | undefined) ??
    '';

  const { schemas: availableTypesForEmpty } = useAvailableTypes(
    apiClients,
    isChildless ? nodesData[0]?.id : null,
  );

  const recentTypesForEmpty = useMemo(
    () => (isChildless && emptyStateOrgId ? getRecentTypes(emptyStateOrgId) : []),
    [isChildless, emptyStateOrgId],
  );

  // Loading state per node (for skeleton display during expand)
  const [loadingNearby, setLoadingNearby] = useState<{ [key: string]: boolean }>({});

  // Wrap expandNode to also track loading state
  const fetchNearbySpheres = useCallback(
    async (sphereId: string, _token?: string, _direction?: string, _surface?: string) => {
      const findLevel = (nodes: SphereNode[], id: string): number => {
        for (const node of nodes) {
          if (node.id === id) return node.level;
          if (node.children.length > 0) {
            const found = findLevel(node.children, id);
            if (found >= 0) return found;
          }
        }
        return -1;
      };
      const level = contextRoots ? findLevel(contextRoots, sphereId) : 0;
      const nextLevel = level >= 0 ? level + 1 : 1;

      setLoadingNearby((prev) => ({ ...prev, [sphereId]: true }));
      try {
        // Infinite-scroll only loads the NEXT page. If there are no more pages,
        // do nothing. Never re-expand here: this handler only runs for already-
        // loaded nodes (callers gate on hasMore), so re-expanding would reload
        // page 1 and reset pagination to hasMore:true — which, with the sentinel
        // still on screen, re-arms this handler into an infinite request loop.
        if (contextHasMore(sphereId)) {
          await contextLoadMore(sphereId, nextLevel);
        }
      } finally {
        setLoadingNearby((prev) => ({ ...prev, [sphereId]: false }));
      }
    },
    [contextRoots, contextHasMore, contextLoadMore]
  );

  // Bridge hasMore: convert from function to object for legacy API
  const hasMore: { [key: string]: boolean } = useMemo(() => {
    if (!contextRoots) return {};
    const result: { [key: string]: boolean } = {};
    const collect = (nodes: SphereNode[]) => {
      for (const node of nodes) {
        if (node.childPagination?.hasMore) {
          result[node.id] = true;
        }
        if (node.children.length > 0) collect(node.children);
      }
    };
    collect(contextRoots);
    return result;
  }, [contextRoots]);

  // Bridge nextTokens: extract from tree
  const nextTokens: { [key: string]: string | undefined } = useMemo(() => {
    if (!contextRoots) return {};
    const result: { [key: string]: string | undefined } = {};
    const collect = (nodes: SphereNode[]) => {
      for (const node of nodes) {
        if (node.childPagination?.nextToken) {
          result[node.id] = node.childPagination.nextToken;
        }
        if (node.children.length > 0) collect(node.children);
      }
    };
    collect(contextRoots);
    return result;
  }, [contextRoots]);

  // Legacy tree manipulation: replaceNodeById operates on Sphere[]
  const replaceNodeById = useCallback(
    (nodes: Sphere[], updatedNode: Sphere): Sphere[] => {
      return nodes.map((node) => {
        if (node.id === updatedNode.id) return updatedNode;
        if (node.nearbySpheres && node.nearbySpheres.length > 0) {
          return { ...node, nearbySpheres: replaceNodeById(node.nearbySpheres, updatedNode) };
        }
        return node;
      });
    },
    []
  );

  // setNodesData shim: for local mutations, trigger a context refresh
  const setNodesData = useCallback(
    (_updater: React.SetStateAction<Sphere[]>) => {
      contextRefresh();
    },
    [contextRefresh]
  );

  // Filter/sort management stubs — context definitions have static filters
  const filterSortState: any = useMemo(() => ({}), []);
  const addOrUpdatePropertyFilter = useCallback(() => { /* no-op */ }, []);
  const addOrUpdatePerspectiveFilter = useCallback(() => { /* no-op */ }, []);
  const removePropertyFilterByKey = useCallback(() => { /* no-op */ }, []);
  const addOrUpdateSortOrder = useCallback(() => { /* no-op */ }, []);
  const removeSortOrderByKey = useCallback(() => { /* no-op */ }, []);
  const resetFilterSortState = useCallback(() => { /* no-op */ }, []);

  // Destructure permissionClient early (before hooks that use it), but use optional chaining to handle null case
  const permissionClient = apiClients?.permissionClient;

  useEffect(() => {
    if (nodesData.length > 0) {
      setBreadcrumbPath(nodesData[0].properties.name ?? t('atlas.breadcrumb.root'));
    }
  }, [nodesData, t]);

  useEffect(() => {
    if (sphereId) {
      setFeaturedSphereId(sphereId);
    }
  }, [sphereId]);

  useEffect(() => {
    if (featuredSphereId) {
      const initialExpandedKeys: { [key: string]: boolean } = {};
      initialExpandedKeys[featuredSphereId] = true;
      setExpandedKeys(initialExpandedKeys);
    }
  }, [featuredSphereId]);

  // Helper function to get the last visible node with children that has more data to fetch
  const getLastNodeWithMoreData = useCallback((): string | null => {
    const findDeepestNodeWithMore = (nodes: Sphere[]): string | null => {
      // Traverse nodes in reverse to find the last visible node with hasMore
      for (let i = nodes.length - 1; i >= 0; i--) {
        const node = nodes[i];

        // Check if this node has children to traverse deeper
        if (node.nearbySpheres && node.nearbySpheres.length > 0) {
          const deeperNode = findDeepestNodeWithMore(node.nearbySpheres);
          if (deeperNode) return deeperNode;
        }

        // Check if this node has more data to fetch
        if (hasMore[node.id]) {
          return node.id;
        }
      }
      return null;
    };

    return findDeepestNodeWithMore(nodesData);
  }, [nodesData, hasMore]);

  // Scroll-to-fetch logic: Load more data when user scrolls near bottom
  useEffect(() => {
    const handleScroll = debounce(() => {
      if (!treeRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = treeRef.current;

      // Check if scrolled near bottom (within 50px)
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        const lastNodeId = getLastNodeWithMoreData();

        if (lastNodeId && hasMore[lastNodeId]) {
          const token = nextTokens[lastNodeId];
          fetchNearbySpheres(lastNodeId, token, 'outwards', firstContext);
        }
      }
    }, 300);

    const scrollContainer = treeRef.current;
    scrollContainer?.addEventListener('scroll', handleScroll);

    return () => {
      scrollContainer?.removeEventListener('scroll', handleScroll);
    };
  }, [treeRef, hasMore, nextTokens, fetchNearbySpheres, getLastNodeWithMoreData, firstContext]);

  const handleShowAddSphereDialog = useCallback((parentSphere?: Sphere, grandparentSphere?: Sphere) => {
    setTemplateDialogVisible(true);
    setNodeParentSphere(parentSphere ?? null);
    setNodeGrandparentSphere(grandparentSphere ?? null);
  }, []);

  const handleShowEditSphereDialog = useCallback((sphere: Sphere, parentSphere: Sphere, viewMode = false) => {
    setEditSphereDialogVisible(true);
    setNodeSphere(sphere);
    setNodeParentSphere(parentSphere);
    setEditViewMode(viewMode);
  }, []);

  const handleHideAddSphereDialog = useCallback(() => {
    setAddSphereDialogVisible(false);
    setSelectedTemplateSchema(null);
  }, []);

  const handleHideEditSphereDialog = useCallback(() => {
    setEditSphereDialogVisible(false);
  }, []);

  const ShowTemplateDialog = useCallback(() => {
    setTemplateDialogVisible(true);
  }, []);

  const HideTemplateDialog = useCallback(() => {
    setTemplateDialogVisible(false);
    setSelectedSphereType(null);
  }, []);

  const handleUseTemplate = (schema: any) => {    
    setSelectedTemplateSchema(schema);
    setAddSphereDialogVisible(true);
    setTemplateDialogVisible(false);
  };

  const handleSphereTypeSelect = useCallback((sphereType: string, source: 'type' | 'usecase', label: string) => {
    setSelectedSphereType({ sphereType, source, label });
  }, []);

  // Pagination functions for Previous/Next navigation
  const handleNextPage = useCallback(async () => {
    if (!treeRef.current) return;

    // Use CSS variable for consistent scroll behavior
    const scrollAmount = Number.parseInt(getComputedStyle(document.documentElement).getPropertyValue('--mobile-scroll-step')) || 200;

    treeRef.current.scrollBy({
      top: scrollAmount,
      behavior: 'smooth',
    });

    // Wait for scroll to complete before fetching
    setTimeout(() => {
      // Fetch more data for the last visible node with more data
      const lastNodeId = getLastNodeWithMoreData();
      if (lastNodeId && hasMore[lastNodeId]) {
        // Check if already fetching for this node
        if (fetchingRef.current[lastNodeId]) {
          return;
        }

        fetchingRef.current[lastNodeId] = true;
        const token = nextTokens[lastNodeId];
        fetchNearbySpheres(lastNodeId, token, 'outwards', firstContext);

        // Reset flag after a delay
        setTimeout(() => {
          fetchingRef.current[lastNodeId] = false;
        }, 1000);
      }
    }, 300);
  }, [getLastNodeWithMoreData, hasMore, nextTokens, fetchNearbySpheres, firstContext]);

  const handlePrevPage = useCallback(async () => {
    if (!treeRef.current) return;

    // Use CSS variable for consistent scroll behavior
    const scrollAmount = Number.parseInt(getComputedStyle(document.documentElement).getPropertyValue('--mobile-scroll-step')) || 200;

    treeRef.current.scrollBy({
      top: -scrollAmount,
      behavior: 'smooth',
    });
  }, []);

  const handleSaveSphere = (newSphere: Sphere, parentId: string) => {
    const connectingPerspective = {
      id: '',
      edgeId: `edge-${Date.now()}`,
      name: 'New Sphere',
      urgency: '1',
      system_name: 'center_center',
      direction: 'outwards',
    };

    newSphere.connectingPerspectives = [...(newSphere.connectingPerspectives || []), connectingPerspective];

    setNodesData((prevNodes) => {
      return prevNodes.map((node) => {
        if (node.id === parentId) {
          const updatedNearbySpheres = [...(node.nearbySpheres || []), newSphere];
          return {
            ...node,
            nearbySpheres: updatedNearbySpheres,
          };
        }
        return node;
      });
    });

    setNodeNotifications((prevNotifications) => ({
      ...prevNotifications,
      [newSphere.id]: {
        type: 'success',
        message: t('atlas.sphere.addSuccess'),
        timer: 5000,
      },
    }));
  };

  const handleSphereRefresh = useCallback(() => {
    void contextRefresh();
  }, [contextRefresh]);

  const handleEditSphere = (updatedSphere: Sphere) => {
    setEditSphereDialogVisible(false);
    setNodesData((prevNodes) => {
      const currentNode = findNodeById(prevNodes, updatedSphere.id);
      if (currentNode) {
        const mergedSphere = {
          ...updatedSphere,
          nearbySpheres: updatedSphere.nearbySpheres && updatedSphere.nearbySpheres.length > 0 ? updatedSphere.nearbySpheres : currentNode.nearbySpheres,
        };
        return replaceNodeById(prevNodes, mergedSphere);
      }
      return prevNodes;
    });
    setNodeNotifications((prevNotifications) => ({
      ...prevNotifications,
      [updatedSphere.id]: {
        type: 'success',
        message: t('atlas.sphere.updateSuccess'),
        timer: 5000,
      },
    }));
  };

  const findNodeById = useCallback((nodes: Sphere[], id: string): Sphere | null => {
    for (const node of nodes) {
      if (node.id === id) {
        return node;
      }
      if (node.nearbySpheres && node.nearbySpheres.length > 0) {
        const foundNode = findNodeById(node.nearbySpheres, id);
        if (foundNode) {
          return foundNode;
        }
      }
    }
    return null;
  }, []);

  const onEdgeCountClick = useCallback(
    (sphereId: string) => {
      setExpandedKeys((prevExpandedKeys) => {
        const isExpanded = prevExpandedKeys[sphereId];
        const newExpandedKeys = { ...prevExpandedKeys };

        if (isExpanded) {
          delete newExpandedKeys[sphereId];
        } else {
          newExpandedKeys[sphereId] = true;
          const node = findNodeById(nodesData, sphereId);
          if (node && (!node.nearbySpheres || node.nearbySpheres.length === 0)) {
            fetchNearbySpheres(sphereId, hasMore[sphereId] ? nextTokens[sphereId] : undefined, 'outwards', firstContext);
          }
        }

        return newExpandedKeys;
      });
    },
    [fetchNearbySpheres, hasMore, nextTokens, findNodeById, nodesData, firstContext]
  );

  const onNodeClickHandler = useCallback(
    (node: Sphere) => {
      setFeaturedSphereId(node.id);
      setNodeNotifications({});

      // Build breadcrumb path
      const findParents = (nodes: Sphere[], childId: string, parentPath: Sphere[] = []): Sphere[] | null => {
        for (const currentNode of nodes) {
          if (currentNode.nearbySpheres && currentNode.nearbySpheres.length > 0) {
            const foundChild = currentNode.nearbySpheres.find((child) => child.id === childId);
            if (foundChild) {
              return [...parentPath, currentNode];
            }
            const result = findParents(currentNode.nearbySpheres, childId, [...parentPath, currentNode]);
            if (result) {
              return result;
            }
          }
        }
        return null;
      };

      const parentPath = findParents(nodesData, node.id);
      if (parentPath) {
        const breadcrumbArray = parentPath.map((parent) => parent.properties.name ?? 'Unknown');
        const fullBreadcrumb = [...breadcrumbArray, node.properties.name ?? 'Unknown'].join(' / ');
        setBreadcrumbPath(fullBreadcrumb);
      }

      setExpandedKeys((prevExpandedKeys) => ({
        ...prevExpandedKeys,
        [node.id]: true,
      }));
    },
    [nodesData]
  );

  const fetchPermissionListHandler = useCallback(
    async (sphereId: string, nodeLabel: string, permissions?: string[]): Promise<PermissionList | null> => {
      if (!sphereId || !nodeLabel || !permissionClient) {
        return null;
      }

      const params = {
        resourceType: nodeLabel,
        resourceId: sphereId,
        permissions,
      };

      try {
        const data = await fetchPermissionList(permissionClient, params);
        return data;
      } catch (err) {
        console.error('Error in fetchPermissionListHandler:', err);
        return null;
      }
    },
    [permissionClient]
  );

  const findAncestorId = useCallback((part: string, ancestorsList: Sphere[]): string | null => {
    for (const ancestor of ancestorsList) {
      if (ancestor.properties?.name === part) {
        return ancestor.id;
      }
      if (ancestor.ancestors && ancestor.ancestors.length > 0) {
        const found = findAncestorId(part, ancestor.ancestors);
        if (found) return found;
      }
    }
    return null;
  }, []);

  const findAncestorsById = useCallback((nodes: Sphere[], id: string): Sphere | null => {
    for (const node of nodes) {
      if (node.id === id) {
        return node;
      }
      if (node.ancestors && node.ancestors.length > 0) {
        const foundNode = findAncestorsById(node.ancestors, id);
        if (foundNode) {
          return foundNode;
        }
      }
    }
    return null;
  }, []);

  const getSphereIdByLabel = useCallback((label: string, nodes: Sphere[]): string => {
    for (const node of nodes) {
      if (node.properties.name === label) {
        return node.id;
      }
      if (node.nearbySpheres && node.nearbySpheres.length > 0) {
        const childResult = getSphereIdByLabel(label, node.nearbySpheres);
        if (childResult !== 'Unknown Sphere ID') {
          return childResult;
        }
      }
    }
    return 'Unknown Sphere ID';
  }, []);

  const handleBreadcrumbClick = useCallback(
    (part: string, event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      // Find sphere ID for the clicked breadcrumb part
      const sphereId = getSphereIdByLabel(part, nodesData);

      if (sphereId && sphereId !== 'Unknown Sphere ID') {
        const node = findNodeById(nodesData, sphereId);
        if (node) {
          setFeaturedSphereId(node.id);
          setExpandedKeys((prevExpandedKeys) => ({
            ...prevExpandedKeys,
            [node.id]: true,
          }));
          // Clear previous children and fetch new data
          node.nearbySpheres = [];
          fetchNearbySpheres(sphereId, undefined, 'outwards', firstContext);
          onNodeClickHandler(node);
        }
      }

      // Find ancestor or sphere by part (for parent nodes in breadcrumb)
      const ancestorId = findAncestorId(part, nodesData);
      const matchedSphereId = getSphereIdByLabel(part, nodesData);

      // Handle ancestor ID or sphere ID navigation
      if (ancestorId && ancestorId !== matchedSphereId) {
        const ancestorNode = findAncestorsById(nodesData, ancestorId);
        if (ancestorNode) {
          setFeaturedSphereId(ancestorNode.id);
          setExpandedKeys((prevExpandedKeys) => ({
            ...prevExpandedKeys,
            [ancestorNode.id]: true,
          }));
          // Clear previous children and fetch new data
          ancestorNode.nearbySpheres = [];
          fetchNearbySpheres(ancestorId, undefined, 'outwards', firstContext);
        }
      }
    },
    [nodesData, findNodeById, onNodeClickHandler, fetchNearbySpheres, firstContext, findAncestorId, findAncestorsById, getSphereIdByLabel]
  );

  const removeNode = useCallback(
    (nodeId: string) => {
      const removeNodeRecursively = (nodes: Sphere[]): Sphere[] => {
        return nodes.reduce<Sphere[]>((acc, node) => {
          if (node.id === nodeId) {
            return acc;
          }
          if (node.nearbySpheres && node.nearbySpheres.length > 0) {
            const updatedChildren = removeNodeRecursively(node.nearbySpheres);
            if (updatedChildren.length === node.nearbySpheres.length) {
              acc.push(node);
            } else {
              acc.push({
                ...node,
                nearbySpheres: updatedChildren,
              });
            }
          } else {
            acc.push(node);
          }
          return acc;
        }, []);
      };

      setNodesData((prevNodes) => removeNodeRecursively(prevNodes));
    },
    [setNodesData]
  );

  // Observe the last child node to trigger fetch when it becomes visible
  const observeLastNearbySphere = useCallback(
    (element: HTMLElement | null, parentId: string) => {
      if (!element) return;

      // Disconnect previous observer for this parent
      if (observersRef.current[parentId]) {
        observersRef.current[parentId].disconnect();
      }

      // Find the scrollable parent container (.mobile-node-children or .mobile-tree-container)
      let scrollableParent: HTMLElement | null = element.parentElement;
      while (scrollableParent && !scrollableParent.classList.contains('mobile-node-children') && !scrollableParent.classList.contains('mobile-tree-container')) {
        scrollableParent = scrollableParent.parentElement;
      }

      // Create new IntersectionObserver
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && hasMore[parentId]) {
            // Check if already fetching for this node
            if (fetchingRef.current[parentId]) {
              return;
            }

            fetchingRef.current[parentId] = true;
            const token = nextTokens[parentId];
            fetchNearbySpheres(parentId, token, 'outwards', firstContext);

            // Reset flag after a delay
            setTimeout(() => {
              fetchingRef.current[parentId] = false;
            }, 1000);
          }
        },
        {
          root: scrollableParent, // Use the immediate scrollable container
          rootMargin: '0px',
          threshold: 0.1, // Trigger when 10% of element is visible
        }
      );

      observer.observe(element);
      observersRef.current[parentId] = observer;
    },
    [hasMore, nextTokens, fetchNearbySpheres, firstContext]
  );

  const renderMobileNode = useCallback(
    (node: Sphere, level: number, isParentNode: boolean, parentNode?: Sphere) => {
      const hasChildren = node.nearbySpheres && node.nearbySpheres.length > 0;

      // Get status for this node (if available)
      const nodeStatus = (node as any).status?.currentValue?.status || 'unknown';
      const statusClass = nodeStatus ? `mobile-tree-node--status-${nodeStatus.replaceAll('_', '-')}` : '';

      return (
        <div key={node.id} className={`mobile-tree-node ${level === 0 ? 'mobile-tree-node--root' : ''}`}>
            <div className={`mobile-node-content ${statusClass}`}>
            <NodeLabel
              sphere={node}
              level={level}
              isParentNode={isParentNode}
              breadcrumbPath={breadcrumbPath}
              onBreadcrumbClick={handleBreadcrumbClick}
              onNodeClick={() => onNodeClickHandler(node)}
              charLimit={50}
              parentNode={parentNode ?? nodesData[0]}
              fetchPermissionList={fetchPermissionListHandler}
              notification={nodeNotifications[node.id]}
              onShowAddSphere={handleShowAddSphereDialog}
              onShowEditSphere={handleShowEditSphereDialog}
              onDeactivateNode={removeNode}
              isExpandedByEdgeClick={false}
              expandedSphereIds={Object.keys(expandedKeys).filter((key) => expandedKeys[key])}
              onChatBubbleClick={onChatBubbleClick}
              isMobile={true}
              mobileMolecules={viewConfig.nodeMolecules}
              organizationId={nodesData[0]?.properties.organization_id}
              showToolbar={showToolbar}
            />
            </div>

          {hasChildren && (
            <div className="mobile-node-children" ref={treeRef}>
              {node.nearbySpheres!.map((childSphere, idx) => {
                const isLastChild = idx === node.nearbySpheres!.length - 1;
                return (
                  <div key={childSphere.id} ref={isLastChild ? (el) => observeLastNearbySphere(el, node.id) : null}>
                    {renderMobileNode(childSphere, level + 1, false, node)}
                  </div>
                );
              })}
              {loadingNearby[node.id] && (
                <div className="mobile-node-loading">
                  <Skeleton width="100%" height="60px" borderRadius="12px" />
                </div>
              )}
            </div>
          )}
        </div>
      );
    },
    [
      expandedKeys,
      loadingNearby,
      breadcrumbPath,
      onNodeClickHandler,
      nodeNotifications,
      handleShowAddSphereDialog,
      handleShowEditSphereDialog,
      removeNode,
      onChatBubbleClick,
      onEdgeCountClick,
      handleBreadcrumbClick,
      fetchPermissionListHandler,
      nodesData,
      observeLastNearbySphere,
    ]
  );

  if (!apiClients) {
    return null;
  }

  return (
    <div className="atlas-tree-mobile">
      <Toast ref={toast} className="custom-toast" />

      <MobileAtlasHeader
        filterSortState={filterSortState}
        featuredSphereId={nodesData[0]?.id}
        resetFilterSortState={resetFilterSortState}
        baseSchema={baseSchema}
        addOrUpdatePropertyFilter={addOrUpdatePropertyFilter}
        addOrUpdatePerspectiveFilter={addOrUpdatePerspectiveFilter}
        removePropertyFilterByKey={removePropertyFilterByKey}
        addOrUpdateSortOrder={addOrUpdateSortOrder}
        removeSortOrderByKey={removeSortOrderByKey}
      />

      <div className="mobile-tree-container">
        {_contextLoading && nodesData.length === 0 ? (
          <div className="mobile-tree-loading">
            <Skeleton width="100%" height="60px" borderRadius="12px" className="mb-2" />
            <Skeleton width="100%" height="60px" borderRadius="12px" className="mb-2" />
            <Skeleton width="100%" height="60px" borderRadius="12px" />
          </div>
        ) : (
          <>
            {nodesData.map((node) => renderMobileNode(node, 0, true))}
            {isChildless && (
              <div className="atlas-empty-state-container">
                <AtlasEmptyStateMolecule
                  sphere={nodesData[0]}
                  orgId={emptyStateOrgId}
                  availableTypes={availableTypesForEmpty}
                  recentTypes={recentTypesForEmpty}
                  onQuickCaptureCapture={(newSphere) =>
                    handleSaveSphere(newSphere, nodesData[0].id)
                  }
                  onOpenDialog={() => handleShowAddSphereDialog(nodesData[0])}
                  onDeploy={() => ShowTemplateDialog()}
                  onBrowseMarketplace={() => ShowTemplateDialog()}
                />
              </div>
            )}
          </>
        )}
        <div className="mobile-tree-footer">
          <div className="mobile-footer-actions">
            <button className="mobile-icon-button" onClick={() => handleShowAddSphereDialog(nodesData[0])}>
              <i className="center-organization-icon"></i>
            </button>
            <button className="mobile-add-button" onClick={ShowTemplateDialog}>
              <span>{t('common.actions.add')}</span>
            </button>
          </div>

          <div className="mobile-pagination">
            <button className="mobile-page-button mobile-page-prev" onClick={handlePrevPage} disabled={treeRef.current?.scrollTop === 0}>
              <i className="pi pi-chevron-left"></i>
              <span>{t('common.pagination.back', { defaultValue: 'Back' })}</span>
            </button>
            <button className="mobile-page-button mobile-page-next" onClick={handleNextPage}>
              <span>{t('common.pagination.next', { defaultValue: 'Next' })}</span>
              <i className="pi pi-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>

      <DialogWrapper
        header={t('sphere.editor.AddSphere')}
        visible={addSphereDialogVisible}
        onHide={handleHideAddSphereDialog}
        className="mobile-dialog"
      >
        <AddSphere
          parentSphere={nodeParentSphere ?? nodesData[0]}
          grandparentSphere={nodeGrandparentSphere || undefined}
          onSave={handleSaveSphere}
          onClose={handleHideAddSphereDialog}
          schema={selectedTemplateSchema}
        />
      </DialogWrapper>

      <DialogWrapper
        header={editViewMode ? t('sphere.editor.viewHeader') : t('sphere.editor.editHeader')}
        visible={editSphereDialogVisible}
        onHide={handleHideEditSphereDialog}
        className="mobile-dialog"
      >
        {nodeSphere && (
          <EditSphere
            sphere={nodeSphere}
            parentSphere={nodeParentSphere ?? nodesData[0]}
            onSave={handleEditSphere}
            onSphereRefresh={handleSphereRefresh}
            onClose={handleHideEditSphereDialog}
            viewMode={editViewMode}
            fetchPermissionList={fetchPermissionListHandler}
          />
        )}
      </DialogWrapper>

      {(() => {
        let templateDialogHeader;
        if (selectedSphereType) {
          if (selectedSphereType.source === 'type') {
            templateDialogHeader = t('addTemplate.TypeSelected', { label: selectedSphereType.label });
          } else {
            templateDialogHeader = t('addTemplate.useCaseSelected', { label: selectedSphereType.label });
          }
        } else {
          templateDialogHeader = t('addTemplate.addTemplateheader');
        }
        return (
          <DialogWrapper
            header={templateDialogHeader}
            visible={templateDialogVisible}
            onHide={() => {
              setSelectedSphereType(null);
              HideTemplateDialog();
            }}
            className="mobile-dialog add-template-dialog"
          >
            <AddTemplate
              onSphereTypeSelect={handleSphereTypeSelect}
              assigneeSystemName={nodesData[0]?.properties?.type ?? ''}
              assigneeNodeLabel={nodesData[0]?.label}
              useCaseFilter={undefined}
              onUseTemplate={handleUseTemplate}
              showPreviewButton={true}
            />
          </DialogWrapper>
        );
      })()}
    </div>
  );
});

AtlasTreeMobile.displayName = 'AtlasTreeMobile';
export default AtlasTreeMobile;
